import { QuestionField } from 'components/questions/QuestionField';
import { ShortAnswerFields } from 'components/questions/ShortAnswerFields';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, GripVertical, Plus, Trash2 } from 'lucide-react';
import { cn } from 'lib/utils';
import { getQuestionOptionLabel } from 'lib/questionOptions';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { AiImageDescriptionField } from 'components/questions/AiImageDescriptionField';

import { RichTextEditor } from 'modules/editor';
import type { QuestionType } from '@examing/types';
import {
  QuestionTypeEditor,
  DISPATCHED_QUESTION_TYPES,
  type QuestionTypeData,
} from 'components/questions/QuestionTypeEditor';
import { nanoid } from 'nanoid';

/**
 * The type-aware authoring body of a question: stem, type-specific editor,
 * answer options/key, and explanation.
 *
 * Presentation matches the Question Bank's single-question editor
 * (pages/QuestionBank/QuestionEditorTwoPane) exactly — same uppercase field
 * labels, same bordered option rows with the success tint and correct-marker,
 * same drag-to-reorder, same ghost "add" buttons — so editing a generated
 * question feels identical to editing a banked one.
 *
 * Fully controlled: the caller owns all state and applies patches.
 */

export interface QuestionAuthoringValue {
  type: QuestionType;
  prompt: string;
  options: { id: string; value: string }[];
  correctAnswer: string | string[] | Record<string, string>;
  matchPairs: { key: string; value: string }[];
  explanation: string;
  typeData: QuestionTypeData;
}

export interface QuestionAuthoringFieldsProps {
  value: QuestionAuthoringValue;
  onChange: (patch: Partial<QuestionAuthoringValue>) => void;
  /** S3 prefix for pasted/uploaded images. */
  uploadContext: string;
  /** Shrinks editor min-heights for dense hosts like the review dialog. */
  compact?: boolean;
  /**
   * Hides the explanation and AI image description editors for payloads with no
   * field to store them (quiz drafts). Everything else — stem, options, answer key — is authored through
   * the rich-text editor everywhere.
   */
  hideExplanation?: boolean;
}

const CHOICE_TYPES: QuestionType[] = ['MULTIPLE_CHOICE', 'MULTIPLE_RESPONSE', 'TRUE_FALSE'];

/** Types whose editor owns a plain-text prompt with inline position markers. */
const PROMPT_OWNED_BY_EDITOR: QuestionType[] = ['FILL_IN_BLANKS', 'PASSAGE'];

/** Matches the two-pane editor's field label treatment. */
const FIELD_LABEL = 'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground';

export const QuestionAuthoringFields: React.FC<QuestionAuthoringFieldsProps> = ({
  value,
  onChange,
  uploadContext,
  compact = false,
  hideExplanation = false,
}) => {
  const { t } = useTranslation();
  const { type, prompt, options, correctAnswer, matchPairs, explanation, typeData } = value;
  const dragIndex = useRef<number | null>(null);
  // Type editors can emit several patches in one event (the passage editor
  // sends pages, text and title back to back). Merge each into the latest
  // type data, not the render's copy, so a later patch cannot drop an earlier one.
  const latestTypeData = useRef(typeData);
  latestTypeData.current = typeData;
  const patchTypeData = (patch: Partial<QuestionTypeData>) => {
    latestTypeData.current = { ...latestTypeData.current, ...patch };
    return latestTypeData.current;
  };

  const isDispatchedType = DISPATCHED_QUESTION_TYPES.includes(type);
  const promptOwnedByEditor = PROMPT_OWNED_BY_EDITOR.includes(type);
  const promptHeight = compact ? 100 : 120;
  const answerHeight = 36;

  const isCorrect = (id: string) =>
    Array.isArray(correctAnswer) ? correctAnswer.includes(id) : correctAnswer === id;

  const markCorrect = (id: string) => {
    if (type === 'MULTIPLE_RESPONSE') {
      const arr = Array.isArray(correctAnswer) ? correctAnswer : [];
      onChange({ correctAnswer: arr.includes(id) ? arr.filter((a) => a !== id) : [...arr, id] });
    } else {
      onChange({ correctAnswer: id });
    }
  };

  const updateOption = (id: string, next: string) =>
    onChange({ options: options.map((o) => (o.id === id ? { ...o, value: next } : o)) });

  const addOption = () =>
    onChange({ options: [...options, { id: `opt_${nanoid(4)}`, value: '' }] });

  const removeOption = (id: string) => {
    const patch: Partial<QuestionAuthoringValue> = { options: options.filter((o) => o.id !== id) };
    // Drop the removed option from the answer key so it can't dangle.
    if (correctAnswer === id) patch.correctAnswer = '';
    if (Array.isArray(correctAnswer)) {
      patch.correctAnswer = correctAnswer.filter((a) => a !== id);
    }
    onChange(patch);
  };

  const reorder = (to: number) => {
    const from = dragIndex.current;
    if (from == null || from === to) return;
    const next = [...options];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    dragIndex.current = null;
    onChange({ options: next });
  };

  return (
    <>
      {/* Stem — hidden for types whose editor owns a plain-text prompt with
          inline blank/hot-text markers (RichText HTML would break the
          character-position logic those types rely on). */}
      {!promptOwnedByEditor && (
        <div className="space-y-1.5">
          <label className={FIELD_LABEL}>{t('question')}</label>
          <QuestionField name="prompt"><RichTextEditor
            value={prompt}
            onChange={(next) => onChange({ prompt: next })}
            preset="question"
            placeholder={t('enter_the_question')}
            uploadContext={uploadContext}
            minHeight={promptHeight}
          /></QuestionField>
        </div>
      )}

      {/* Type-specific authoring UI for extended question types */}
      {isDispatchedType && (
        <QuestionTypeEditor
          type={type}
          prompt={prompt}
          onPromptChange={(next) => onChange({ prompt: next })}
          data={typeData}
          onChange={(patch) => onChange({ typeData: patchTypeData(patch) })}
        />
      )}

      {/* Answer options (MC / MR / TF) */}
      {CHOICE_TYPES.includes(type) && (
        <QuestionField name="correctAnswer"><div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <label className={FIELD_LABEL}>{t('answer_options')}</label>
            <span className="text-xs text-muted-foreground">
              {type === 'MULTIPLE_RESPONSE' ? t('options_select_all') : t('editor_mark_correct')}
            </span>
          </div>
          <QuestionField name="options"><div className="space-y-2">
            {options.map((opt, i) => {
              const correct = isCorrect(opt.id);
              const canDrag = type !== 'TRUE_FALSE';
              return (
                <div
                  key={opt.id}
                  draggable={canDrag}
                  onDragStart={() => (dragIndex.current = i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => reorder(i)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-lg border px-1.5 py-1.5 transition-colors sm:gap-2 sm:px-2.5 sm:py-2',
                    correct
                      ? 'border-success-500 bg-success-50/70 dark:bg-success-900/20'
                      : 'border-border bg-card',
                  )}
                >
                  {/* Native HTML5 drag handle — touch devices don't fire it, so
                      it's hidden on phones and the width goes to the input. */}
                  {canDrag && (
                    <GripVertical className="hidden h-4 w-4 shrink-0 cursor-grab text-muted-foreground/50 sm:block" />
                  )}
                  {/* The -m-1.5/p-1.5 pair keeps the tap target at 28px while the
                      drawn control shrinks to 16px on phones. */}
                  <button
                    type="button"
                    onClick={() => markCorrect(opt.id)}
                    aria-label={t('mark_as_correct')}
                    aria-pressed={correct}
                    className="-m-1.5 shrink-0 p-1.5"
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 items-center justify-center border transition-colors sm:h-5 sm:w-5',
                        // Square marker = checkbox (matches ui/checkbox), circle = radio.
                        type === 'MULTIPLE_RESPONSE' ? 'rounded-[4px]' : 'rounded-full',
                        correct
                          ? 'border-success-600 bg-success-600 text-white'
                          : 'border-input',
                      )}
                    >
                      {correct &&
                        (type === 'MULTIPLE_RESPONSE' ? (
                          <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" strokeWidth={3} />
                        ) : (
                          <span className="h-1.5 w-1.5 rounded-full bg-white sm:h-2 sm:w-2" />
                        ))}
                    </span>
                  </button>
                  <div className="min-w-0 flex-1">
                    {type === 'TRUE_FALSE' ? (
                      <span className="text-sm text-foreground">{getQuestionOptionLabel(type, opt.value, t)}</span>
                    ) : (
                      <QuestionField name={`options.${i}.value`}><RichTextEditor
                        value={opt.value}
                        onChange={(v) => updateOption(opt.id, v)}
                        preset="answer"
                        placeholder={t('option')}
                        uploadContext={uploadContext}
                        minHeight={answerHeight}
                      /></QuestionField>
                    )}
                  </div>
                  {/* The filled control + success border already read as
                      "correct"; the word is desktop-only so the input keeps the
                      width on phones. */}
                  {correct && (
                    <span className="hidden shrink-0 text-xs font-semibold text-success-700 dark:text-success-400 sm:inline">
                      {t('editor_correct')}
                    </span>
                  )}
                  {canDrag && options.length > 2 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 text-muted-foreground sm:h-7 sm:w-7"
                      onClick={() => removeOption(opt.id)}
                      aria-label={t('delete')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div></QuestionField>
          {type !== 'TRUE_FALSE' && options.length < 6 && (
            <Button variant="ghost" size="sm" onClick={addOption}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              {t('add_option')}
            </Button>
          )}
        </div></QuestionField>
      )}

      {/* Short answer */}
      {type === 'SHORT_ANSWER' && <ShortAnswerFields data={typeData} correctAnswer={correctAnswer}
        onChange={(patch, answer) => onChange({ typeData: patchTypeData(patch), ...(answer !== undefined ? { correctAnswer: answer } : {}) })} />}

      {/* Matching */}
      {type === 'MATCHING' && (
        <QuestionField name="matchPairs"><div className="space-y-2">
          <label className={FIELD_LABEL}>{t('match_pairs')}</label>
          {matchPairs.map((pair, i) => (
            <div key={i} className="flex items-center gap-2">
              <QuestionField name={`matchPairs.${i}.key`} className="flex-1"><Input
                placeholder={t('term')}
                value={pair.key}
                onChange={(e) =>
                  onChange({
                    matchPairs: matchPairs.map((p, j) =>
                      j === i ? { ...p, key: e.target.value } : p,
                    ),
                  })
                }
              /></QuestionField>
              <span className="text-muted-foreground">→</span>
              <QuestionField name={`matchPairs.${i}.value`} className="flex-1"><Input
                placeholder={t('definition')}
                value={pair.value}
                onChange={(e) =>
                  onChange({
                    matchPairs: matchPairs.map((p, j) =>
                      j === i ? { ...p, value: e.target.value } : p,
                    ),
                  })
                }
              /></QuestionField>
              {matchPairs.length > 2 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onChange({ matchPairs: matchPairs.filter((_, j) => j !== i) })}
                  aria-label={t('delete')}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange({ matchPairs: [...matchPairs, { key: '', value: '' }] })}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t('add_pair')}
          </Button>
        </div></QuestionField>
      )}

      {/* Explanation — quiz payloads have no field for it. */}
      <div className={cn('space-y-1.5', hideExplanation && 'hidden')}>
        <label className={FIELD_LABEL}>{t('explanation_optional')}</label>
        <RichTextEditor
          value={explanation}
          onChange={(next) => onChange({ explanation: next })}
          preset="answer"
          placeholder={t('explain_correct_answer')}
          uploadContext={uploadContext}
          minHeight={compact ? 60 : 80}
        />
      </div>

      {/* AI-only picture description — stored on the same payloads as the explanation. */}
      <div className={cn(hideExplanation && 'hidden')}>
        <AiImageDescriptionField
          value={typeData.aiImageDescription ?? ''}
          onChange={(next) => onChange({ typeData: patchTypeData({ aiImageDescription: next }) })}
        />
      </div>
    </>
  );
};

export default QuestionAuthoringFields;
